/**
 * Grading Setup Page (super-admin / manage_admin) — 4 tabs:
 *   - Thresholds: per-KPI grade range editor
 *   - Weights: per-KPI weight, sum=100 validation
 *   - City Constraints: compression maps per market
 *   - Recommended Actions: per-KPI action bullets shown when KPI is the
 *     primary issue on a property
 *
 * Mounted at /setup/grading. Linked from sidebar under COMPANY SETUP.
 */
import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import { formatMoney } from '../../utils/money';

const KPIS = [
  { key: 'profit_margin',       label: 'Profit Margin',       direction: 'higher_better', valueKind: 'rate' },
  { key: 'expense_ratio',       label: 'Expense Ratio',       direction: 'lower_better',  valueKind: 'rate' },
  { key: 'true_no_charge_rate', label: 'True No Charge Rate', direction: 'lower_better',  valueKind: 'rate' },
  { key: 'courtesy_tow_rate',   label: 'Courtesy Tow Rate',   direction: 'lower_better',  valueKind: 'rate' },
  { key: 'tow_fee',             label: 'Tow Fee',             direction: 'higher_better', valueKind: 'dollar' },
];

const GRADES = ['A+','A','A-','B+','B','B-','C+','C','C-','D+','D','D-','F'];

// Helpers — convert between stored decimals and display strings.
// Rates are stored as 0.0500 = 5%; UI shows 5.00 (no % sign in inputs).
// Dollars are stored as raw numbers; UI shows the raw number.
function toDisplay(val, kind) {
  if (val == null || val === '') return '';
  if (kind === 'rate') return (Number(val) * 100).toFixed(2);
  return String(val);
}
function fromDisplay(str, kind) {
  if (str === '' || str == null) return null;
  const n = Number(str);
  if (!Number.isFinite(n)) return null;
  return kind === 'rate' ? n / 100 : n;
}
function valueLabel(kind) {
  return kind === 'rate' ? '%' : '$';
}

// Tab buttons — small reusable chip group
function TabBar({ tabs, current, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
      {tabs.map(t => {
        const active = current === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              padding: '10px 18px', border: 'none', background: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              color: active ? '#1a1a2e' : '#6b7280',
              borderBottom: active ? '3px solid #D4A843' : '3px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 1: Thresholds
// ──────────────────────────────────────────────────────────────────
function ThresholdsTab() {
  const [kpi, setKpi] = useState(KPIS[0].key);
  const [byKpi, setByKpi] = useState({});
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const kpiMeta = KPIS.find(k => k.key === kpi);

  const load = useCallback(async () => {
    const data = await api.getGradingThresholds();
    setByKpi(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setRows((byKpi[kpi] || []).map(r => ({ ...r })));
    setDirty(false);
  }, [byKpi, kpi]);

  const updateRow = (i, field, value) => {
    setRows(prev => prev.map((r, j) => j === i ? { ...r, [field]: value } : r));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r, i) => ({
        grade: r.grade,
        min_value: r.min_value,
        max_value: r.max_value,
        direction: r.direction || kpiMeta.direction,
        sort_order: i,
        is_active: r.is_active === 0 ? 0 : 1,
      }));
      await api.saveGradingThresholds(kpi, payload);
      await load();
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    setRows(prev => [...prev, {
      grade: 'F',
      min_value: null,
      max_value: null,
      direction: kpiMeta.direction,
      is_active: 1,
    }]);
    setDirty(true);
  };

  const removeRow = (i) => {
    setRows(prev => prev.filter((_, j) => j !== i));
    setDirty(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>KPI:</label>
        <select value={kpi} onChange={e => setKpi(e.target.value)}
          className="sales-input" style={{ fontSize: 13, padding: '6px 10px' }}>
          {KPIS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          Direction: <strong>{kpiMeta.direction === 'higher_better' ? 'Higher is better' : 'Lower is better'}</strong>
        </span>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          Values in <strong>{valueLabel(kpiMeta.valueKind)}</strong>
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={addRow} className="btn"
            style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
            + Add Row
          </button>
          <button onClick={save} disabled={saving || !dirty} className="btn btn-primary"
            style={{ backgroundColor: dirty ? '#D4A843' : '#d1d5db', color: '#1a1a2e', fontWeight: 600 }}>
            {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, fontStyle: 'italic' }}>
        Min is inclusive, Max is exclusive. Leave Min blank for "down to negative infinity" (used on F band when lower_better)
        or Max blank for "up to infinity" (used on top band when higher_better).
      </div>

      <table className="data-table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ width: 80 }}>Grade</th>
            <th>Min ({valueLabel(kpiMeta.valueKind)})</th>
            <th>Max ({valueLabel(kpiMeta.valueKind)})</th>
            <th style={{ width: 80 }}>Active</th>
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <select value={r.grade} onChange={e => updateRow(i, 'grade', e.target.value)}
                  className="sales-input" style={{ fontSize: 13 }}>
                  {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </td>
              <td>
                <input
                  type="number" step="any"
                  value={toDisplay(r.min_value, kpiMeta.valueKind)}
                  onChange={e => updateRow(i, 'min_value', fromDisplay(e.target.value, kpiMeta.valueKind))}
                  placeholder="—"
                  className="sales-input" style={{ fontSize: 13, width: 120 }}
                />
              </td>
              <td>
                <input
                  type="number" step="any"
                  value={toDisplay(r.max_value, kpiMeta.valueKind)}
                  onChange={e => updateRow(i, 'max_value', fromDisplay(e.target.value, kpiMeta.valueKind))}
                  placeholder="—"
                  className="sales-input" style={{ fontSize: 13, width: 120 }}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={r.is_active !== 0}
                  onChange={e => updateRow(i, 'is_active', e.target.checked ? 1 : 0)}
                />
              </td>
              <td>
                <button onClick={() => removeRow(i)} style={{
                  background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16,
                }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 2: Weights
// ──────────────────────────────────────────────────────────────────
function WeightsTab() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    const data = await api.getGradingWeights();
    setRows(data);
    setDirty(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateRow = (i, field, value) => {
    setRows(prev => prev.map((r, j) => j === i ? { ...r, [field]: value } : r));
    setDirty(true);
  };

  const activeSum = rows
    .filter(r => r.is_active !== 0)
    .reduce((s, r) => s + Number(r.weight || 0), 0);
  const sumOk = Math.abs(activeSum - 100) < 0.01;

  const save = async () => {
    if (!sumOk) { alert(`Active weights must sum to 100 (currently ${activeSum.toFixed(2)})`); return; }
    setSaving(true);
    try {
      await api.saveGradingWeights(rows.map((r, i) => ({
        kpi_key: r.kpi_key,
        weight: Number(r.weight),
        label: r.label,
        is_active: r.is_active === 0 ? 0 : 1,
        sort_order: i,
      })));
      await load();
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{
          fontSize: 13, fontWeight: 700,
          color: sumOk ? '#10b981' : '#ef4444',
        }}>
          Active sum: {activeSum.toFixed(2)}/100 {sumOk ? '✓' : '✗'}
        </span>
        <button onClick={save} disabled={saving || !dirty || !sumOk}
          className="btn btn-primary" style={{
            marginLeft: 'auto',
            backgroundColor: dirty && sumOk ? '#D4A843' : '#d1d5db',
            color: '#1a1a2e', fontWeight: 600,
          }}>
          {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
        </button>
      </div>

      <table className="data-table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>KPI</th>
            <th style={{ width: 130 }}>Weight (%)</th>
            <th style={{ width: 80 }}>Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.kpi_key}>
              <td>
                <strong>{r.label}</strong>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.kpi_key}</div>
              </td>
              <td>
                <input
                  type="number" step="0.01" min="0" max="100"
                  value={r.weight}
                  onChange={e => updateRow(i, 'weight', e.target.value)}
                  className="sales-input" style={{ fontSize: 13, width: 110 }}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={r.is_active !== 0}
                  onChange={e => updateRow(i, 'is_active', e.target.checked ? 1 : 0)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 3: City Constraints
// ──────────────────────────────────────────────────────────────────
function CitiesTab() {
  const [cities, setCities] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setCities(await api.getGradingCities());
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setDraft({
      city_name: c.city_name,
      market_ceiling_grade: c.market_ceiling_grade,
      compression_map: c.compression_map || {},
      is_active: c.is_active,
      notes: c.notes || '',
      default_tow_fee: c.default_tow_fee != null ? String(c.default_tow_fee) : '',
      is_core_market: c.is_core_market === 1,
    });
  };

  const startNew = () => {
    setEditingId('new');
    // Default compression: identity map (no compression)
    const identity = {};
    GRADES.forEach(g => { identity[g] = g; });
    setDraft({
      city_name: '',
      market_ceiling_grade: 'A+',
      compression_map: identity,
      is_active: 1,
      notes: '',
      default_tow_fee: '',
      is_core_market: false,
    });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const updateMap = (rawGrade, finalGrade) => {
    setDraft(d => ({ ...d, compression_map: { ...d.compression_map, [rawGrade]: finalGrade } }));
  };

  const saveCity = async () => {
    setSaving(true);
    try {
      // Convert empty fee string → null. Server accepts null to clear.
      const payload = {
        ...draft,
        default_tow_fee: draft.default_tow_fee === '' ? null : Number(draft.default_tow_fee),
      };
      delete payload.is_core_market; // server-managed flag, not editable
      if (editingId === 'new') {
        await api.createGradingCity(payload);
      } else {
        await api.updateGradingCity(editingId, payload);
      }
      await load();
      cancelEdit();
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const removeCity = async (id) => {
    if (!window.confirm('Delete this city constraint? Properties in this city will fall back to no compression.')) return;
    try { await api.deleteGradingCity(id); await load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={startNew} className="btn"
          style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
          + Add City
        </button>
      </div>

      {editingId && draft && (
        <div style={{
          padding: 16, marginBottom: 16, borderRadius: 10,
          backgroundColor: draft.is_core_market ? '#eff6ff' : '#fffbeb',
          border: `1px solid ${draft.is_core_market ? '#bfdbfe' : '#fde68a'}`,
        }}>
          {draft.is_core_market && (
            <div style={{
              fontSize: 11, color: '#1d4ed8', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8,
            }}>
              Editing the Core Market Default — only the default tow fee is editable
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>City Name</label>
              <input value={draft.is_core_market ? 'All Other Cities' : draft.city_name}
                disabled={draft.is_core_market}
                onChange={e => setDraft({...draft, city_name: e.target.value})}
                className="sales-input" style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Market Ceiling</label>
              <select value={draft.market_ceiling_grade}
                disabled={draft.is_core_market}
                onChange={e => setDraft({...draft, market_ceiling_grade: e.target.value})}
                className="sales-input" style={{ width: '100%', fontSize: 13 }}>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Default Tow Fee ($)</label>
              <input type="number" step="0.01" min="0"
                value={draft.default_tow_fee}
                onChange={e => setDraft({...draft, default_tow_fee: e.target.value})}
                placeholder={draft.is_core_market ? '272' : 'optional'}
                className="sales-input" style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Active</label>
              <div style={{ paddingTop: 6 }}>
                <input type="checkbox" checked={draft.is_active !== 0}
                  disabled={draft.is_core_market}
                  onChange={e => setDraft({...draft, is_active: e.target.checked ? 1 : 0})} />
              </div>
            </div>
          </div>

          {!draft.is_core_market && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Compression Map (raw grade → final displayed grade)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 6 }}>
                {GRADES.map(raw => (
                  <div key={raw} style={{
                    padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, backgroundColor: '#fff',
                    fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <span style={{ fontWeight: 700, width: 24 }}>{raw}</span>
                    <span style={{ color: '#9ca3af' }}>→</span>
                    <select value={draft.compression_map[raw] || raw}
                      onChange={e => updateMap(raw, e.target.value)}
                      className="sales-input" style={{ fontSize: 12, flex: 1, padding: '2px 4px' }}>
                      {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea rows={2} value={draft.notes}
              onChange={e => setDraft({...draft, notes: e.target.value})}
              className="sales-input" style={{ width: '100%', fontSize: 13 }} />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={cancelEdit} className="btn">Cancel</button>
            <button onClick={saveCity} disabled={saving} className="btn btn-primary"
              style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <table className="data-table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>City</th>
            <th style={{ width: 120 }}>Ceiling</th>
            <th style={{ width: 130 }}>Default Tow Fee</th>
            <th style={{ width: 80 }}>Active</th>
            <th>Notes</th>
            <th style={{ width: 140 }}></th>
          </tr>
        </thead>
        <tbody>
          {cities.map(c => {
            const isCore = c.is_core_market === 1;
            return (
              <tr key={c.id} style={isCore ? { backgroundColor: '#eff6ff' } : undefined}>
                <td>
                  {isCore ? (
                    <>
                      <strong>All Other Cities</strong>
                      <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>Core Market Default</div>
                    </>
                  ) : (
                    <strong>{c.city_name}</strong>
                  )}
                </td>
                <td>{c.market_ceiling_grade}</td>
                <td style={{ fontWeight: 600 }}>
                  {c.default_tow_fee != null ? formatMoney(c.default_tow_fee) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td>{c.is_active ? '✓' : '—'}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{c.notes || '—'}</td>
                <td>
                  <button onClick={() => startEdit(c)} className="btn btn-sm">Edit</button>
                  {!isCore && (
                    <button onClick={() => removeCity(c.id)} style={{
                      background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, marginLeft: 8,
                    }}>Delete</button>
                  )}
                </td>
              </tr>
            );
          })}
          {cities.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af' }}>No city constraints loaded.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 4: Recommended Actions
// ──────────────────────────────────────────────────────────────────
function ActionsTab() {
  const [byKpi, setByKpi] = useState({});
  const [kpi, setKpi] = useState(KPIS[0].key);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setByKpi(await api.getGradingActions());
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setRows((byKpi[kpi] || []).map(r => ({ ...r })));
    setDirty(false);
  }, [byKpi, kpi]);

  const updateRow = (i, field, value) => {
    setRows(prev => prev.map((r, j) => j === i ? { ...r, [field]: value } : r));
    setDirty(true);
  };
  const addRow = () => {
    setRows(prev => [...prev, { action_text: '', is_active: 1 }]);
    setDirty(true);
  };
  const removeRow = (i) => {
    setRows(prev => prev.filter((_, j) => j !== i));
    setDirty(true);
  };
  const move = (i, dir) => {
    const newRows = [...rows];
    const target = i + dir;
    if (target < 0 || target >= newRows.length) return;
    [newRows[i], newRows[target]] = [newRows[target], newRows[i]];
    setRows(newRows);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.saveGradingActions(kpi, rows.map((r, i) => ({
        action_text: r.action_text,
        sort_order: i,
        is_active: r.is_active === 0 ? 0 : 1,
      })));
      await load();
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>KPI:</label>
        <select value={kpi} onChange={e => setKpi(e.target.value)}
          className="sales-input" style={{ fontSize: 13, padding: '6px 10px' }}>
          {KPIS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={addRow} className="btn"
            style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
            + Add Action
          </button>
          <button onClick={save} disabled={saving || !dirty} className="btn btn-primary"
            style={{ backgroundColor: dirty ? '#D4A843' : '#d1d5db', color: '#1a1a2e', fontWeight: 600 }}>
            {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, fontStyle: 'italic' }}>
        These actions show on a property's grade panel when this KPI is the primary issue.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button onClick={() => move(i, -1)} disabled={i === 0}
                style={{ background: 'none', border: 'none', cursor: i === 0 ? 'not-allowed' : 'pointer', fontSize: 11, color: i === 0 ? '#d1d5db' : '#6b7280', padding: 0 }}>▲</button>
              <button onClick={() => move(i, 1)} disabled={i === rows.length - 1}
                style={{ background: 'none', border: 'none', cursor: i === rows.length - 1 ? 'not-allowed' : 'pointer', fontSize: 11, color: i === rows.length - 1 ? '#d1d5db' : '#6b7280', padding: 0 }}>▼</button>
            </div>
            <input value={r.action_text}
              onChange={e => updateRow(i, 'action_text', e.target.value)}
              placeholder="Recommended action text…"
              className="sales-input" style={{ flex: 1, fontSize: 13 }} />
            <label style={{ fontSize: 11, color: '#6b7280' }}>
              <input type="checkbox" checked={r.is_active !== 0}
                onChange={e => updateRow(i, 'is_active', e.target.checked ? 1 : 0)} />
              {' '}Active
            </label>
            <button onClick={() => removeRow(i)} style={{
              background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16,
            }}>✕</button>
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            No actions yet for this KPI. Click "+ Add Action" above.
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 5: Import Tow Counts (Parking Snap CSV → property_tow_counts)
// ──────────────────────────────────────────────────────────────────
function ImportTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (dryRun) => {
    if (!file) { setError('Pick a CSV first.'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.bulkImportTowCounts(fd, dryRun);
      setResult(r);
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{
        padding: 16, marginBottom: 14, borderRadius: 10,
        backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 6 }}>
          Parking Snap export format
        </div>
        <div style={{ fontSize: 12, color: '#1e3a8a', lineHeight: 1.6 }}>
          One row per individual tow. Required columns: <code>Enforced Date</code>{' '}
          (MM/DD/YYYY or YYYY-MM-DD) and <code>Property</code> (matched
          case-insensitively against existing property names).
          The importer aggregates tows per (property, year, month) and upserts
          into <code>property_tow_counts</code>. Re-running the same file is
          safe — existing rows overwrite, never duplicate.
        </div>
      </div>

      <div style={{
        padding: 16, borderRadius: 10, backgroundColor: '#fff',
        border: '1px solid #e5e7eb', marginBottom: 14,
      }}>
        <input
          type="file" accept=".csv,text/csv"
          onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError(''); }}
          style={{ marginBottom: 12, display: 'block' }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => submit(true)} disabled={!file || busy}
            className="btn"
            style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
            {busy ? 'Working…' : 'Preview (Dry Run)'}
          </button>
          <button onClick={() => submit(false)} disabled={!file || busy}
            className="btn btn-primary"
            style={{ backgroundColor: '#10b981', color: '#fff', fontWeight: 600 }}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fee2e2', color: '#991b1b', fontSize: 13 }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{
          padding: 16, borderRadius: 10,
          backgroundColor: result.dry_run ? '#fffbeb' : '#dcfce7',
          border: `1px solid ${result.dry_run ? '#fde68a' : '#86efac'}`,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700,
            color: result.dry_run ? '#92400e' : '#166534',
            textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8,
          }}>
            {result.dry_run ? 'Preview — nothing saved' : 'Import complete'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 12 }}>
            <Stat label="File" value={result.filename} small />
            <Stat label="CSV Rows" value={result.total_rows} />
            <Stat label="Property-Months" value={result.property_months_aggregated} />
            <Stat label="Matched" value={result.properties_matched} ok />
            <Stat label="Unmatched" value={result.properties_unmatched}
              warn={result.properties_unmatched > 0} />
            <Stat label="Tows Matched" value={result.tows_matched} />
            <Stat label="Tows Unmatched" value={result.tows_unmatched}
              warn={result.tows_unmatched > 0} />
            {!result.dry_run && (
              <Stat label="Saved Rows" value={result.imported_property_months} ok />
            )}
            {result.invalid_dates > 0 && (
              <Stat label="Invalid Dates" value={result.invalid_dates} warn />
            )}
          </div>

          {result.unmatched_property_names && result.unmatched_property_names.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{
                cursor: 'pointer', fontSize: 12, fontWeight: 700,
                color: '#92400e', marginBottom: 8,
              }}>
                {result.unmatched_property_names.length} unmatched property name{result.unmatched_property_names.length === 1 ? '' : 's'} (click to expand)
              </summary>
              <div style={{
                marginTop: 8, padding: 10, backgroundColor: '#fff',
                borderRadius: 6, border: '1px solid #fde68a',
                maxHeight: 240, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace',
              }}>
                {result.unmatched_property_names.map(n => (
                  <div key={n} style={{ padding: '2px 0' }}>{n}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#92400e', marginTop: 6, fontStyle: 'italic' }}>
                Fix by renaming the property in Properties admin (Company Setup → Properties)
                OR cleaning the source CSV before re-uploading.
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, ok, warn, small }) {
  const color = ok ? '#166534' : warn ? '#991b1b' : '#111827';
  const bg   = ok ? '#dcfce7' : warn ? '#fee2e2' : '#f9fafb';
  return (
    <div style={{ padding: '8px 12px', borderRadius: 8, backgroundColor: bg }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: small ? 12 : 18, fontWeight: 700, color, marginTop: 2, wordBreak: 'break-word' }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Page shell
// ──────────────────────────────────────────────────────────────────
export default function GradingSetupPage() {
  const [tab, setTab] = useState('thresholds');

  const tabs = [
    { key: 'thresholds', label: 'Grade Thresholds' },
    { key: 'weights',    label: 'Weights' },
    { key: 'cities',     label: 'City Constraints' },
    { key: 'actions',    label: 'Recommended Actions' },
    { key: 'import',     label: 'Import Tow Counts' },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            Property Grading Setup
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
            Configure KPI thresholds, weights, city constraints, and recommended actions
          </p>
        </div>
      </div>

      <TabBar tabs={tabs} current={tab} onChange={setTab} />
      {tab === 'thresholds' && <ThresholdsTab />}
      {tab === 'weights' && <WeightsTab />}
      {tab === 'cities' && <CitiesTab />}
      {tab === 'actions' && <ActionsTab />}
      {tab === 'import' && <ImportTab />}
    </div>
  );
}
